import { List, Tooltip } from 'antd';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ReactComponent as SmartFill } from 'assets/svgs/smartfill.svg';
import { Button } from 'components/atoms/button';
import { Popover } from 'components/atoms/popover';
import { useTranslation } from 'hooks/use-translation';
import { CK_VARIABLES_TYPE_SELECT_POPUP } from 'vendor/ckeditor/plugins/variables/types';
import { StyledList } from './custom-styles';

const VariablesSelection = ({
  editor,
  onSelect,
}: {
  editor?: any;
  onSelect: () => void;
}) => {
  const onSimpleVariablesSelect = useCallback(() => {
    if (editor == null) {
      return;
    }
    editor.editing.view.focus();

    editor.model.change((writer) => {
      editor.model.insertContent(writer.createText(' {{'));
      editor.editing.view.focus();
    });
    onSelect();
  }, [editor, onSelect]);

  const onConditionalVariablesSelect = useCallback(() => {
    if (editor == null) {
      return;
    }
    editor.editing.view.focus();

    editor.model.change((writer) => {
      editor.model.insertContent(writer.createText(' {%'));
      editor.editing.view.focus();
    });
    onSelect();
  }, [editor, onSelect]);

  return (
    <StyledList header={<FormattedMessage id="variables" />} split={false}>
      <List.Item onClick={onSimpleVariablesSelect}>
        <List.Item.Meta
          title={<FormattedMessage id="variables.simple.title" />}
          description={<FormattedMessage id="variables.simple.title.caption" />}
        />
        <>
          <span>{'{'}</span>
          <span>{'{'}</span>
        </>
      </List.Item>
      <List.Item onClick={onConditionalVariablesSelect}>
        <List.Item.Meta
          title={<FormattedMessage id="variables.conditional.title" />}
          description={
            <FormattedMessage id="variables.conditional.title.caption" />
          }
        />
        <>
          <span>{'{'}</span>
          <span>%</span>
        </>
      </List.Item>
    </StyledList>
  );
};

interface Props {
  editor?: any;
  btnClassName?: string;
  forceShowPopover?: boolean;
}

const StyledPopover = styled(Popover)`
  .ant-popover-inner-content {
    padding: var(--spacing-03) 0;
  }
`;

const PopoverTriggerButton = styled(Button)`
  width: 32px !important;
  min-width: 32px !important;
  height: 32px !important;
  padding: var(--spacing-02) !important;
  margin-right: 0 var(--spacing-02) !important;

  &.project-template-trigger {
    font-size: var(--font-size-sm);
  }

  &.smartfill-icon {
    svg {
      height: 20px;
      width: 20px;
    }
  }
`;

export const VariablesSelect: FC<Props> = ({
  editor,
  btnClassName = '',
  forceShowPopover,
}) => {
  const variablesCaption = useTranslation('toolbar.variables');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (forceShowPopover) {
      setVisible(true);
    }
  }, [forceShowPopover]);

  const hide = () => {
    setVisible(false);
  };

  const onVisibleChange = (_visible: boolean) => {
    setVisible(_visible);
  };

  return (
    <StyledPopover
      trigger="click"
      placement="bottom"
      visible={visible}
      className={CK_VARIABLES_TYPE_SELECT_POPUP}
      onVisibleChange={onVisibleChange}
      content={
        <VariablesSelection
          editor={editor}
          onSelect={() => {
            hide();
          }}
        />
      }
    >
      <Tooltip title={variablesCaption}>
        <PopoverTriggerButton
          className={btnClassName}
          type="text"
          aria-label={variablesCaption}
          icon={<SmartFill />}
        />
      </Tooltip>
    </StyledPopover>
  );
};

export const ProjectVariablesTrigger = ({ editor }: { editor?: any }) => {
  const variablesCaption = useTranslation('toolbar.variables');
  const onClick = useCallback(() => {
    if (editor == null) {
      return;
    }
    editor.editing.view.focus();

    editor.model.change((writer) => {
      editor.model.insertContent(writer.createText(' {{'));
      editor.editing.view.focus();
    });
  }, [editor]);

  return (
    <Tooltip title={variablesCaption}>
      <Button
        data-cy="variables.project_variables.trigger"
        style={{ fontSize: 'var(--font-size-sm)' }}
        type="text"
        aria-label={variablesCaption}
        icon={<SmartFill />}
        onClick={onClick}
      />
    </Tooltip>
  );
};
